import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { hasEffectivePermission } from '../utils/accessControl';
import ProjectForm from '../components/ProjectForm';
import '../styles/Projects.css';

const STATUS_COLORS = {
  planning: '#f59f28',
  in_progress: '#40a853',
  completed: '#2f78c9',
  on_hold: '#d15252',
};

const STATUS_LABELS = {
  planning: 'Planlama',
  in_progress: 'Devam Ediyor',
  completed: 'Tamamlandı',
  on_hold: 'Askıda',
};

const ALLOWED_FILTERS = new Set(['all', 'planning', 'in_progress', 'completed', 'on_hold']);

const Projects = () => {
  const { user, permissions } = useAuth();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const API_BASE = API_BASE_URL;
  const canManageProjects = hasEffectivePermission(user, permissions, 'can_manage_projects');

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`${API_BASE}/projects`);
      setProjects(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      setError('Projeler yüklenirken hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const status = String(searchParams.get('status') || '').trim().toLowerCase();
    if (!status) {
      setFilter('all');
      return;
    }
    setFilter(ALLOWED_FILTERS.has(status) ? status : 'all');
  }, [location.search]);

  const handleCreateProject = async (projectData) => {
    if (!canManageProjects) return;
    try {
      await apiClient.post(`${API_BASE}/projects`, projectData);
      fetchProjects();
      setShowForm(false);
      setEditingProject(null);
    } catch (requestError) {
      setError('Proje oluşturulurken hata oluştu.');
    }
  };

  const handleUpdateProject = async (id, projectData) => {
    if (!canManageProjects) return;
    try {
      await apiClient.put(`${API_BASE}/projects/${id}`, projectData);
      fetchProjects();
      setEditingProject(null);
      setShowForm(false);
    } catch (requestError) {
      setError('Proje güncellenirken hata oluştu.');
    }
  };

  const handleDeleteProject = async (id) => {
    if (!canManageProjects) return;
    if (!window.confirm('Bu projeyi silmek istediğinizden emin misiniz?')) return;
    try {
      await apiClient.delete(`${API_BASE}/projects/${id}`);
      fetchProjects();
    } catch (requestError) {
      setError('Proje silinirken hata oluştu.');
    }
  };

  const startEditing = (project) => {
    if (!canManageProjects) return;
    setEditingProject(project);
    setShowForm(true);
  };

  const filteredProjects = projects.filter((project) => {
    const matchesFilter = filter === 'all' || project.status === filter;
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return matchesFilter;
    const nameMatch = String(project.name || '').toLowerCase().includes(normalizedSearch);
    const locationMatch = String(project.location || '').toLowerCase().includes(normalizedSearch);
    return matchesFilter && (nameMatch || locationMatch);
  });

  if (loading) {
    return <div className="projects-container"><p className="loading">Yükleniyor...</p></div>;
  }

  return (
    <div className="projects-container">
      <div className="projects-header">
        <h2>Projeler</h2>
        {canManageProjects && (
          <button
            className="btn-primary"
            onClick={() => {
              setEditingProject(null);
              setShowForm(true);
            }}
            type="button"
          >
            + Yeni Proje Ekle
          </button>
        )}
      </div>

      {error && <div className="error-alert">{error}</div>}

      {showForm && (
        <div className="form-modal">
          <div className="form-content">
            <ProjectForm
              initialData={editingProject}
              onSubmit={editingProject
                ? (data) => handleUpdateProject(editingProject.id, data)
                : handleCreateProject}
              onCancel={() => {
                setShowForm(false);
                setEditingProject(null);
              }}
            />
          </div>
        </div>
      )}

      <div className="projects-controls">
        <input
          type="text"
          placeholder="Proje adı veya konuma göre ara..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          className="search-input"
        />
        <div className="filter-buttons">
          <button className={filter === 'all' ? 'filter-btn active' : 'filter-btn'} onClick={() => setFilter('all')} type="button">Tümü</button>
          <button className={filter === 'planning' ? 'filter-btn active' : 'filter-btn'} onClick={() => setFilter('planning')} type="button">Planlama</button>
          <button className={filter === 'in_progress' ? 'filter-btn active' : 'filter-btn'} onClick={() => setFilter('in_progress')} type="button">Devam Ediyor</button>
          <button className={filter === 'completed' ? 'filter-btn active' : 'filter-btn'} onClick={() => setFilter('completed')} type="button">Tamamlandı</button>
        </div>
      </div>

      {filteredProjects.length === 0 ? (
        <div className="no-projects">
          <p>Proje bulunamadı.</p>
        </div>
      ) : (
        <div className="projects-grid">
          {filteredProjects.map((project) => (
            <div key={project.id} className="project-card">
              <div className="project-card-header">
                <h3>
                  <Link to={`/projects/${project.id}`}>{project.name}</Link>
                </h3>
                <span className="status-badge" style={{ backgroundColor: STATUS_COLORS[project.status] || '#8b9fb8' }}>
                  {STATUS_LABELS[project.status] || project.status}
                </span>
              </div>

              <div className="project-card-body">
                <p><strong>Konum:</strong> {project.location || 'Belirtilmemiş'}</p>
                <p><strong>Başlangıç:</strong> {project.start_date ? new Date(project.start_date).toLocaleDateString('tr-TR') : '-'}</p>
                <p><strong>Bitiş:</strong> {project.end_date ? new Date(project.end_date).toLocaleDateString('tr-TR') : '-'}</p>
                <p><strong>Bütçe:</strong> {project.budget ? `₺${parseFloat(project.budget).toLocaleString('tr-TR')}` : '-'}</p>
              </div>

              <div className="project-card-footer">
                <Link to={`/projects/${project.id}`} className="btn-card-action" title="Detaylar">Detaylar</Link>
                {canManageProjects && (
                  <button className="btn-card-action" onClick={() => startEditing(project)} title="Düzenle" type="button">Düzenle</button>
                )}
                {canManageProjects && (
                  <button className="btn-card-action danger" onClick={() => handleDeleteProject(project.id)} title="Sil" type="button">Sil</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Projects;
